import { createAuctisyncClient, createWebStorageAdapter } from '@auctisync/api-client';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Check apps/web/.env.local.',
  );
}

/**
 * The shared AuctiSync client, built here because this is the ONE place that may
 * read the web app's env. `@auctisync/api-client` is framework-neutral — it takes
 * url/anonKey/storage injected — so the same client code runs in the native apps
 * with a Keychain storage adapter instead (Run Brief P0.5 / D14).
 *
 * `detectSessionInUrl: true` is REQUIRED here and is not the package default:
 * the browser completes magic-link and OAuth sign-ins by reading the session out
 * of the redirect URL. Native uses a deep link + PKCE instead, hence the
 * safe-by-default `false` in the package.
 *
 * Storage stays browser `localStorage` under Supabase's usual key, pinned to the
 * project ref rather than read from whatever address is in use, so existing
 * signed-in sessions carry over untouched — including when the address changes
 * (move P2: set VITE_SUPABASE_PROJECT_REF with the new address). The functions
 * address is its own setting, VITE_FUNCTIONS_URL; unset, it is today's.
 */
export const apiClient = createAuctisyncClient({
  url,
  anonKey,
  storage: createWebStorageAdapter(),
  detectSessionInUrl: true,
  ...(import.meta.env.VITE_SUPABASE_PROJECT_REF
    ? { projectRef: import.meta.env.VITE_SUPABASE_PROJECT_REF }
    : {}),
  ...(import.meta.env.VITE_FUNCTIONS_URL
    ? { functionsUrl: import.meta.env.VITE_FUNCTIONS_URL }
    : {}),
});

/**
 * Single Supabase client for the browser, typed against the generated `Database`
 * schema. Auth session is persisted in localStorage and refreshed automatically
 * by the SDK. Regenerate types with `pnpm db:types` after any migration.
 *
 * Unchanged for callers: every existing `import { supabase } from '…/lib/supabase'`
 * keeps working exactly as before. Bidding is the one exception — it must go
 * through `apiClient.placeBid` and never invoke the bid RPC off this client
 * directly (CI-enforced by `scripts/check-bid-contract.mjs`, whose text scan this
 * comment is deliberately worded to avoid tripping).
 */
export const supabase = apiClient.supabase;
